import { useEffect, useCallback } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import { useWebSocketStore } from '../store/websocket-store';
import {
  STREAMING_CONNECTION_CLOSED_SSE_EVENT,
  STREAMING_CONNECTION_OPENED_SSE_EVENT,
  STREAMING_MESSAGE_RECEIVED_SSE_EVENT,
  type StreamingConnectionClosedPayload,
  type StreamingConnectionOpenedPayload,
  type StreamingMessageReceivedPayload,
} from '@terra/shared-contracts-interact';

function findConnectionIdBySession(sessionId: string): string | undefined {
  const { connections } = useWebSocketStore.getState();
  return Object.keys(connections).find(
    (id) => connections[id].streamingSessionId === sessionId,
  );
}

export function useStreamingSSE() {
  const { subscribe } = useSSE();

  const handleConnectionOpened = useCallback(
    (event: StreamingConnectionOpenedPayload) => {
      const { upsertAgentConnection } = useWebSocketStore.getState();
      const { sessionId, url, connectedAt } = event;
      upsertAgentConnection({
        streamingSessionId: sessionId,
        url,
        source: 'copilot',
        status: 'open',
        connectedAt,
      });
    },
    [],
  );

  const handleMessageReceived = useCallback(
    (event: StreamingMessageReceivedPayload) => {
      const { upsertAgentConnection, addAgentMessage } =
        useWebSocketStore.getState();
      const { sessionId, message } = event;

      const connectionId = findConnectionIdBySession(sessionId);
      if (connectionId) {
        const conn = useWebSocketStore.getState().connections[connectionId];
        if (conn?.status === 'closed') return;
      }

      const resolvedConnectionId =
        connectionId ??
        upsertAgentConnection({
          streamingSessionId: sessionId,
          url: '',
          source: 'copilot',
          status: 'open',
        });

      addAgentMessage({
        connectionId: resolvedConnectionId,
        data: message.data,
        direction: message.direction === 'outbound' ? 'sent' : 'received',
        timestamp: message.timestamp,
      });
    },
    [],
  );

  const handleConnectionClosed = useCallback(
    (event: StreamingConnectionClosedPayload) => {
      const { updateConnection } = useWebSocketStore.getState();
      const connectionId = findConnectionIdBySession(event.sessionId);
      if (connectionId) {
        updateConnection(connectionId, { status: 'closed' });
      }
    },
    [],
  );

  useEffect(() => {
    const unsubscribers = [
      subscribe(STREAMING_CONNECTION_OPENED_SSE_EVENT, handleConnectionOpened),
      subscribe(STREAMING_MESSAGE_RECEIVED_SSE_EVENT, handleMessageReceived),
      subscribe(STREAMING_CONNECTION_CLOSED_SSE_EVENT, handleConnectionClosed),
    ];
    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [
    subscribe,
    handleConnectionOpened,
    handleMessageReceived,
    handleConnectionClosed,
  ]);
}
